import React from "react";
import { useLocation } from "react-router-dom";
import { useApp } from "../store";

const SITE_NAME = "SharkX Electric";

function titleCase(input = "") {
  return input
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function ensureTag(selector, create) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(name, value, attr = "name") {
  if (value == null) return;
  const selector = `meta[${attr}="${name}"]`;
  const el = ensureTag(selector, () => {
    const node = document.createElement("meta");
    node.setAttribute(attr, name);
    return node;
  });
  el.setAttribute("content", value);
}

function setLink(rel, href) {
  if (!href) return;
  const el = ensureTag(`link[rel="${rel}"]`, () => {
    const node = document.createElement("link");
    node.setAttribute("rel", rel);
    return node;
  });
  el.setAttribute("href", href);
}

function resolveTitle(pathname, t) {
  const p = pathname.replace(/\/$/, "") || "/";
  const parts = p.split("/").filter(Boolean);
  const [section, slug] = parts;

  if (p === "/" || p === "/home-final") return `${SITE_NAME} — Electric Water Sports`;
  if (p === "/book" || p === "/book-demo") return `${t.nav.book} | ${SITE_NAME}`;
  if (p === "/blog") return `${t.nav.blog || t.home.journal || "Blog"} | ${SITE_NAME}`;
  if (section === "blog" && slug) return `${t.blogPosts?.[slug]?.title || titleCase(slug)} | ${SITE_NAME}`;
  if (p === "/contact") return `${t.nav.contact || "Contact"} | ${SITE_NAME}`;
  if (p === "/faq") return `${t.nav.faq || "FAQ"} | ${SITE_NAME}`;
  if (p === "/support") return `${t.nav.support || "Support"} | ${SITE_NAME}`;
  if (p === "/warranty") return `${t.nav.warranty || "Warranty"} | ${SITE_NAME}`;
  if (p === "/cart") return `${t.nav.cart || "Cart"} | ${SITE_NAME}`;
  if (p === "/ultra-ai") return `${t.nav.ultra || "SharkX Ultra AI"} | ${SITE_NAME}`;
  if (p === "/compare") return `${t.comparePage?.title || "Compare"} | ${SITE_NAME}`;
  if (p === "/wishlist") return `${t.wishlist?.title || "Wishlist"} | ${SITE_NAME}`;
  if (p === "/admin/login") return `Admin Login | ${SITE_NAME}`;
  if (p === "/products/efoils") return `${t.catPages?.efoils?.heroTitle || t.nav.efoils} | ${SITE_NAME}`;
  if (p === "/products/jetboards") return `${t.catPages?.jetboards?.heroTitle || t.nav.jetboards} | ${SITE_NAME}`;
  if (p === "/products/accessories") return `${t.nav.accessories || "Accessories"} | ${SITE_NAME}`;
  if (p === "/products/ultra_ai" || p === "/products/sharkx-ultra-ai-electric-jetski" || p === "/products/SharkX-Ultra-AI-electric-jetski") return `${t.nav.ultra || "SharkX Ultra AI"} | ${SITE_NAME}`;
  if (section === "products" && slug) return `${titleCase(slug)} | ${SITE_NAME}`;
  if (parts[0] === "product" && parts[1]) return `${titleCase(parts.slice(1).join(" "))} | ${SITE_NAME}`;
  return `${SITE_NAME} — Electric Water Sports`;
}

function resolveDescription(pathname, t) {
  const p = pathname.replace(/\/$/, "") || "/";
  if (p === "/") return t.hero.subtitle;
  if (p === "/book" || p === "/book-demo") return t.book.heroBody;
  if (p === "/blog") return `${t.home.journal} · ${t.home.storiesGuides}`;
  if (p.startsWith("/blog/")) return t.home.storiesGuides;
  if (p === "/contact") return t.contact.body;
  if (p === "/support") return t.supportPage.heroBody;
  if (p === "/warranty") return t.warranty.summary;
  if (p === "/ultra-ai") return t.ultra.body;
  if (p === "/cart") return t.cart.description || t.hero.subtitle;
  if (p === "/products/efoils") return t.catPages?.efoils?.heroSub;
  if (p === "/products/jetboards") return t.catPages?.jetboards?.heroSub;
  if (p === "/products/accessories") return t.catPages?.accessories?.heroSub || t.nav.accessories;
  return t.hero.subtitle;
}

export default function SeoHead() {
  const { pathname } = useLocation();
  const { t } = useApp();

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const title = resolveTitle(pathname, t);
    const description = resolveDescription(pathname, t);
    const canonical = `${window.location.origin}${pathname.replace(/\/$/, "") || "/"}`;
    const stagingHost = window.location.hostname.includes("staging");

    document.title = title;
    setMeta("title", title);
    setMeta("description", description);
    setMeta("robots", stagingHost ? "noindex, nofollow, noarchive" : "index, follow");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("og:url", canonical, "property");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setLink("canonical", canonical);
  }, [pathname, t]);

  return null;
}